import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { validatePhoneNumber, cleanPhoneNumber } from '../utils/validation';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, patientLogin } = useAuth();
  const [tab, setTab] = useState(0);
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const navigate = useNavigate();
  const [phoneError, setPhoneError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and common phone separators
    const cleanedValue = value.replace(/[^\d\s\-\(\)]/g, '');
    setPhone(cleanedValue);
    
    // Validate phone number
    const validation = validatePhoneNumber(cleanedValue);
    if (!validation.isValid && cleanedValue.length > 0) {
      setPhoneError(validation.error);
    } else {
      setPhoneError('');
    }
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number before submission
    const phoneValidation = validatePhoneNumber(phone);
    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await patientLogin(phoneValidation.cleanPhone, dob);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
              Patho Probe
            </Typography>
            <Typography variant="h6" component="h2" gutterBottom align="center" color="textSecondary">
              Sign In
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Tabs value={tab} onChange={(e, v) => setTab(v)} centered sx={{ mb: 2 }}>
              <Tab label="User Login" />
              <Tab label="Patient Login" />
            </Tabs>
            {tab === 0 && (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Box>
            )}
            {tab === 1 && (
              <Box component="form" onSubmit={handlePatientSubmit} sx={{ mt: 2 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="phone"
                  label="Phone"
                  name="phone"
                  autoComplete="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  error={!!phoneError}
                  helperText={phoneError || 'Enter 10-digit phone number'}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="dob"
                  label="Date of Birth (YYYY-MM-DD)"
                  type="date"
                  id="dob"
                  InputLabelProps={{ shrink: true }}
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login; 